// src/pages/SharedCodebookPage.tsx
// Public viewer for shared Bookmate tutorials — no login required

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CodebookGetResponse } from '../types/chat';
import codebookApi from '../services/codebookApi';
import PublicCodebookViewer from '../components/Codebook/PublicCodebookViewer';

const SharedCodebookPage: React.FC = () => {
  const { codebookId } = useParams<{ codebookId: string }>();
  const navigate = useNavigate();
  const [codebook, setCodebook] = useState<CodebookGetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!codebookId) {
      setError('Invalid link');
      setLoading(false);
      return;
    }

    const fetchCodebook = async () => {
      try {
        const data = await codebookApi.getPublicCodebook(codebookId);
        setCodebook(data);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('This tutorial is not available. It may have been removed or is no longer shared.');
        } else {
          setError('Failed to load tutorial. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCodebook();
  }, [codebookId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Loading tutorial...</p>
        </div>
      </div>
    );
  }

  if (error || !codebook) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="border-b border-border bg-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <img
                  src="/cubemate-logo.png"
                  alt="Cubemate Logo"
                  className="w-8 h-8 rounded-lg"
                />
                <span className="text-lg font-semibold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent">
                  Cubemate
                </span>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all text-sm font-medium"
              >
                Create your own
              </button>
            </div>
          </div>
        </header>

        {/* Error state */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md px-4">
            <div className="text-4xl">404</div>
            <h2 className="text-xl font-semibold text-foreground">Tutorial not found</h2>
            <p className="text-muted-foreground">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Go to homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img
                src="/cubemate-logo.png"
                alt="Cubemate Logo"
                className="w-8 h-8 rounded-lg"
              />
              <span className="text-lg font-semibold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent">
                Cubemate
              </span>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all text-sm font-medium"
            >
              Create your own
            </button>
          </div>
        </div>
      </header>

      {/* Codebook content */}
      <div className="flex-1 overflow-hidden">
        <PublicCodebookViewer
          codebook={codebook}
          onBack={() => navigate('/')}
        />
      </div>
    </div>
  );
};

export default SharedCodebookPage;
